import { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useToast } from '@/hooks/use-toast';
import {
  Facebook, Instagram, Linkedin, Youtube, ChevronLeft, ChevronRight,
  Plus, Send, GripVertical, Clock, Loader2, X, Image as ImageIcon, Upload, Trash2,
} from 'lucide-react';
import SEO from '@/components/SEO';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface SocialPost {
  id: string;
  marketing_output_id: string | null;
  platform: string;
  content: string;
  image_prompt: string | null;
  image_url: string | null;
  status: string;
  scheduled_for: string | null;
  published_at: string | null;
  created_at: string;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const PLATFORM_CONFIG: Record<string, { icon: typeof Facebook; label: string; bg: string; border: string; text: string }> = {
  facebook:          { icon: Facebook,  label: 'Facebook',                      bg: 'bg-blue-50',   border: 'border-blue-300', text: 'text-blue-700' },
  instagram:         { icon: Instagram, label: 'Instagram',                     bg: 'bg-pink-50',   border: 'border-pink-300', text: 'text-pink-700' },
  linkedin:          { icon: Linkedin,  label: 'LinkedIn, Brian (Personal)',   bg: 'bg-amber-50',  border: 'border-amber-300', text: 'text-amber-800' },
  linkedin_personal: { icon: Linkedin,  label: 'LinkedIn, Brian (Personal)',   bg: 'bg-amber-50',  border: 'border-amber-300', text: 'text-amber-800' },
  linkedin_company:  { icon: Linkedin,  label: 'LinkedIn, SeniorThrive (Co)', bg: 'bg-indigo-50', border: 'border-indigo-300', text: 'text-indigo-700' },
  youtube:           { icon: Youtube,   label: 'YouTube',                       bg: 'bg-red-50',    border: 'border-red-300', text: 'text-red-700' },
};

const STATUS_BADGE: Record<string, { className: string; label: string }> = {
  pending:        { className: 'bg-yellow-100 text-yellow-800 border-yellow-300', label: 'Pending' },
  queued:         { className: 'bg-yellow-100 text-yellow-800 border-yellow-300', label: 'Queued' },
  awaiting_image: { className: 'bg-yellow-100 text-yellow-800 border-yellow-300', label: 'Awaiting Image' },
  approved:       { className: 'bg-green-100 text-green-800 border-green-300',    label: 'Approved' },
  published:      { className: 'bg-green-600 text-white border-green-700',        label: 'Published' },
  rejected:       { className: 'bg-red-100 text-red-800 border-red-300',          label: 'Rejected' },
  failed:         { className: 'bg-red-100 text-red-800 border-red-300',          label: 'Failed' },
};

const SUPABASE_URL = 'https://plizcqmxjmtzdgmmhtxj.supabase.co';
const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const HOURS = Array.from({ length: 14 }, (_, i) => i + 7); // 7am - 8pm

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getMonday(d: Date): Date {
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1);
  const monday = new Date(d);
  monday.setDate(diff);
  monday.setHours(0, 0, 0, 0);
  return monday;
}

function addDays(d: Date, n: number): Date {
  const r = new Date(d);
  r.setDate(r.getDate() + n);
  return r;
}

function formatDate(d: Date): string {
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function formatWeekRange(monday: Date): string {
  const sunday = addDays(monday, 6);
  const mMonth = monday.toLocaleDateString('en-US', { month: 'long' });
  const sMonth = sunday.toLocaleDateString('en-US', { month: 'long' });
  const year = monday.getFullYear();
  if (mMonth === sMonth) {
    return `${mMonth} ${monday.getDate()} - ${sunday.getDate()}, ${year}`;
  }
  return `${formatDate(monday)} - ${formatDate(sunday)}, ${year}`;
}

function isSameDay(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

function toLocalISOString(d: Date): string {
  const pad = (n: number) => n.toString().padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}:00`;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function ContentCalendar() {
  const { toast } = useToast();
  const [posts, setPosts] = useState<SocialPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [weekStart, setWeekStart] = useState(() => getMonday(new Date()));
  const [editPost, setEditPost] = useState<SocialPost | null>(null);
  const [editContent, setEditContent] = useState('');
  const [quickPostOpen, setQuickPostOpen] = useState(false);
  const [quickPostData, setQuickPostData] = useState({ platform: 'facebook', content: '', scheduled_for: '' });
  const [quickPostImage, setQuickPostImage] = useState<File | null>(null);
  const [quickPostImagePreview, setQuickPostImagePreview] = useState<string | null>(null);
  const [editPostImage, setEditPostImage] = useState<File | null>(null);
  const [editPostImagePreview, setEditPostImagePreview] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [dragPost, setDragPost] = useState<SocialPost | null>(null);

  // Ref for drop target tracking
  const dropTargetRef = useRef<{ dayIndex: number; hour: number } | null>(null);
  const quickPostFileRef = useRef<HTMLInputElement>(null);
  const editPostFileRef = useRef<HTMLInputElement>(null);

  // ── Fetch posts ──
  const fetchPosts = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('social_content_queue')
      .select('*')
      .not('status', 'eq', 'archived')
      .order('created_at', { ascending: false })
      .limit(200);

    if (error) {
      toast({ title: 'Error loading posts', description: error.message, variant: 'destructive' });
    } else {
      setPosts((data as SocialPost[]) || []);
    }
    setLoading(false);
  }, [toast]);

  useEffect(() => { fetchPosts(); }, [fetchPosts]);

  // ── Derived data ──
  const weekDays = useMemo(() => {
    return Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
  }, [weekStart]);

  const scheduledPosts = useMemo(() => {
    return posts.filter(p => p.scheduled_for);
  }, [posts]);

  const unscheduledPosts = useMemo(() => {
    return posts.filter(p => !p.scheduled_for && p.status !== 'published' && p.status !== 'rejected');
  }, [posts]);

  const getPostsForSlot = useCallback((dayIndex: number, hour: number) => {
    const day = weekDays[dayIndex];
    if (!day) return [];
    return scheduledPosts.filter(p => {
      const d = new Date(p.scheduled_for!);
      return isSameDay(d, day) && d.getHours() === hour;
    });
  }, [weekDays, scheduledPosts]);

  const todayApproved = useMemo(() => {
    const today = new Date();
    return scheduledPosts.filter(p => {
      if (p.status !== 'approved' || !p.scheduled_for) return false;
      return isSameDay(new Date(p.scheduled_for), today);
    });
  }, [scheduledPosts]);

  // ── Actions ──
  const updateSchedule = useCallback(async (postId: string, scheduled_for: string) => {
    const { error } = await supabase
      .from('social_content_queue')
      .update({ scheduled_for } as any)
      .eq('id', postId);
    if (error) {
      toast({ title: 'Failed to reschedule', description: error.message, variant: 'destructive' });
    } else {
      setPosts(prev => prev.map(p => p.id === postId ? { ...p, scheduled_for } : p));
      toast({ title: 'Post rescheduled' });
    }
  }, [toast]);

  const uploadImageFile = useCallback(async (file: File): Promise<string | null> => {
    const ext = file.name.split('.').pop() || 'png';
    const storagePath = `social/upload-${Date.now()}.${ext}`;
    const { error } = await supabase.storage
      .from('marketing-images')
      .upload(storagePath, file, { contentType: file.type, upsert: true });
    if (error) {
      toast({ title: 'Image upload failed', description: error.message, variant: 'destructive' });
      return null;
    }
    return `${SUPABASE_URL}/storage/v1/object/public/marketing-images/${storagePath}`;
  }, [toast]);

  const saveEdit = useCallback(async () => {
    if (!editPost) return;
    setSaving(true);

    let imageUrl = editPost.image_url;
    if (editPostImage) {
      const uploaded = await uploadImageFile(editPostImage);
      if (uploaded) imageUrl = uploaded;
    }

    const updatePayload: any = { content: editContent };
    if (imageUrl !== editPost.image_url) updatePayload.image_url = imageUrl;

    const { error } = await supabase
      .from('social_content_queue')
      .update(updatePayload)
      .eq('id', editPost.id);
    if (error) {
      toast({ title: 'Save failed', description: error.message, variant: 'destructive' });
    } else {
      setPosts(prev => prev.map(p => p.id === editPost.id ? { ...p, content: editContent, image_url: imageUrl } : p));
      toast({ title: 'Post updated' });
      setEditPost(null);
      setEditPostImage(null);
      setEditPostImagePreview(null);
    }
    setSaving(false);
  }, [editPost, editContent, editPostImage, toast, uploadImageFile]);

  const createQuickPost = useCallback(async () => {
    if (!quickPostData.content.trim()) return;
    setSaving(true);

    let imageUrl: string | null = null;
    if (quickPostImage) {
      imageUrl = await uploadImageFile(quickPostImage);
    }

    const payload: any = {
      platform: quickPostData.platform,
      content: quickPostData.content,
      status: 'pending',
    };
    if (imageUrl) payload.image_url = imageUrl;
    if (quickPostData.scheduled_for) {
      payload.scheduled_for = new Date(quickPostData.scheduled_for).toISOString();
    }
    const { data, error } = await supabase
      .from('social_content_queue')
      .insert(payload)
      .select()
      .single();
    if (error) {
      toast({ title: 'Failed to create post', description: error.message, variant: 'destructive' });
    } else {
      setPosts(prev => [data as SocialPost, ...prev]);
      toast({ title: 'Post created' });
      setQuickPostOpen(false);
      setQuickPostData({ platform: 'facebook', content: '', scheduled_for: '' });
      setQuickPostImage(null);
      setQuickPostImagePreview(null);
    }
    setSaving(false);
  }, [quickPostData, quickPostImage, toast, uploadImageFile]);

  const publishAllApproved = useCallback(async () => {
    if (todayApproved.length === 0) return;
    setPublishing(true);
    const ids = todayApproved.map(p => p.id);
    const { error } = await supabase
      .from('social_content_queue')
      .update({ status: 'published', published_at: new Date().toISOString() } as any)
      .in('id', ids);
    if (error) {
      toast({ title: 'Publish failed', description: error.message, variant: 'destructive' });
    } else {
      setPosts(prev => prev.map(p =>
        ids.includes(p.id) ? { ...p, status: 'published', published_at: new Date().toISOString() } : p
      ));
      toast({ title: `Published ${ids.length} post(s)` });
    }
    setPublishing(false);
  }, [todayApproved, toast]);

  // ── Drag & Drop ──
  const handleDragStart = (post: SocialPost) => {
    setDragPost(post);
  };

  const handleDragOver = (e: React.DragEvent, dayIndex: number, hour: number) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    dropTargetRef.current = { dayIndex, hour };
  };

  const handleDrop = (e: React.DragEvent, dayIndex: number, hour: number) => {
    e.preventDefault();
    if (!dragPost) return;
    const targetDay = weekDays[dayIndex];
    const targetDate = new Date(targetDay);
    targetDate.setHours(hour, 0, 0, 0);
    updateSchedule(dragPost.id, targetDate.toISOString());
    setDragPost(null);
    dropTargetRef.current = null;
  };

  // ── Render helpers ──
  const getPlatformConfig = (platform: string) =>
    PLATFORM_CONFIG[platform] || PLATFORM_CONFIG.facebook;

  const getStatusBadge = (status: string) =>
    STATUS_BADGE[status] || STATUS_BADGE.pending;

  const PostCard = ({ post, compact = false }: { post: SocialPost; compact?: boolean }) => {
    const plat = getPlatformConfig(post.platform);
    const stat = getStatusBadge(post.status);
    const Icon = plat.icon;

    return (
      <div
        draggable
        onDragStart={() => handleDragStart(post)}
        onClick={() => { setEditPost(post); setEditContent(post.content); }}
        className={`cursor-pointer rounded-md border ${plat.border} ${plat.bg} p-2 mb-1 hover:shadow-md transition-shadow group`}
      >
        <div className="flex items-start gap-1.5">
          <GripVertical className="h-3.5 w-3.5 mt-0.5 text-gray-400 opacity-0 group-hover:opacity-100 flex-shrink-0 cursor-grab" />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5 mb-1">
              <Icon className={`h-3.5 w-3.5 ${plat.text} flex-shrink-0`} />
              <span className={`text-[10px] font-medium ${plat.text} truncate`}>{plat.label}</span>
              <span className={`text-[9px] px-1.5 py-0.5 rounded-full border ${stat.className} ml-auto flex-shrink-0`}>
                {stat.label}
              </span>
            </div>
            {post.image_url && !compact && (
              <div className="mb-1 rounded overflow-hidden h-12">
                <img
                  src={post.image_url}
                  alt=""
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>
            )}
            <p className="text-[11px] text-gray-700 line-clamp-2 leading-tight">
              {post.content.slice(0, 80)}{post.content.length > 80 ? '...' : ''}
            </p>
            {post.scheduled_for && (
              <div className="flex items-center gap-1 mt-1 text-[10px] text-gray-500">
                <Clock className="h-3 w-3" />
                {new Date(post.scheduled_for).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  // ── Navigation ──
  const goToday = () => setWeekStart(getMonday(new Date()));
  const goPrev = () => setWeekStart(prev => addDays(prev, -7));
  const goNext = () => setWeekStart(prev => addDays(prev, 7));

  const today = new Date();

  return (
    <>
      <SEO title="Content Calendar | Admin" />
      <div className="flex h-[calc(100vh-4rem)] overflow-hidden">
        {/* ── Main Calendar Area ── */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b bg-white flex-shrink-0">
            <div className="flex items-center gap-3">
              <h1 className="text-xl font-bold">Content Calendar</h1>
              <div className="flex items-center gap-1">
                <Button variant="outline" size="icon" onClick={goPrev} className="h-8 w-8">
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button variant="outline" size="sm" onClick={goToday} className="h-8">
                  Today
                </Button>
                <Button variant="outline" size="icon" onClick={goNext} className="h-8 w-8">
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
              <span className="text-sm text-muted-foreground font-medium">
                {formatWeekRange(weekStart)}
              </span>
            </div>
            <div className="flex items-center gap-2">
              {todayApproved.length > 0 && (
                <Button
                  onClick={publishAllApproved}
                  disabled={publishing}
                  size="sm"
                  className="bg-green-600 hover:bg-green-700"
                >
                  {publishing ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Send className="h-4 w-4 mr-1" />}
                  Publish All Approved ({todayApproved.length})
                </Button>
              )}
              <Button onClick={() => setQuickPostOpen(true)} size="sm">
                <Plus className="h-4 w-4 mr-1" /> Quick Post
              </Button>
            </div>
          </div>

          {/* Calendar Grid */}
          {loading ? (
            <div className="flex-1 flex items-center justify-center">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <div className="flex-1 overflow-auto">
              <div className="min-w-[800px]">
                {/* Day headers */}
                <div className="grid grid-cols-[60px_repeat(7,1fr)] border-b bg-gray-50 sticky top-0 z-10">
                  <div className="p-2" />
                  {weekDays.map((day, i) => {
                    const isToday = isSameDay(day, today);
                    return (
                      <div
                        key={i}
                        className={`p-2 text-center border-l ${isToday ? 'bg-blue-50' : ''}`}
                      >
                        <div className="text-xs text-muted-foreground font-medium">{DAYS[i]}</div>
                        <div className={`text-sm font-semibold ${isToday ? 'bg-blue-600 text-white rounded-full w-7 h-7 flex items-center justify-center mx-auto' : ''}`}>
                          {day.getDate()}
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Time slots */}
                {HOURS.map(hour => (
                  <div key={hour} className="grid grid-cols-[60px_repeat(7,1fr)] border-b min-h-[80px]">
                    <div className="p-1 text-[11px] text-muted-foreground text-right pr-2 pt-1 border-r">
                      {hour === 0 ? '12 AM' : hour < 12 ? `${hour} AM` : hour === 12 ? '12 PM' : `${hour - 12} PM`}
                    </div>
                    {weekDays.map((_, dayIndex) => {
                      const slotPosts = getPostsForSlot(dayIndex, hour);
                      const isToday = isSameDay(weekDays[dayIndex], today);
                      return (
                        <div
                          key={dayIndex}
                          className={`border-l p-1 transition-colors ${isToday ? 'bg-blue-50/30' : ''} ${dragPost ? 'hover:bg-blue-100/50' : ''}`}
                          onDragOver={(e) => handleDragOver(e, dayIndex, hour)}
                          onDrop={(e) => handleDrop(e, dayIndex, hour)}
                        >
                          {slotPosts.map(post => (
                            <PostCard key={post.id} post={post} />
                          ))}
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* ── Unscheduled Sidebar ── */}
        <div className="w-64 border-l bg-gray-50 flex flex-col flex-shrink-0">
          <div className="p-3 border-b bg-white">
            <h2 className="text-sm font-semibold flex items-center gap-2">
              <Clock className="h-4 w-4" />
              Unscheduled
              <Badge variant="secondary" className="ml-auto">{unscheduledPosts.length}</Badge>
            </h2>
          </div>
          <ScrollArea className="flex-1">
            <div className="p-2 space-y-1">
              {unscheduledPosts.length === 0 ? (
                <p className="text-xs text-muted-foreground text-center py-4">
                  All posts are scheduled
                </p>
              ) : (
                unscheduledPosts.map(post => (
                  <PostCard key={post.id} post={post} compact />
                ))
              )}
            </div>
          </ScrollArea>
        </div>
      </div>

      {/* ── Edit Dialog ── */}
      <Dialog open={!!editPost} onOpenChange={(open) => {
        if (!open) { setEditPost(null); setEditPostImage(null); setEditPostImagePreview(null); }
      }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {editPost && (() => {
                const plat = getPlatformConfig(editPost.platform);
                const Icon = plat.icon;
                return (
                  <>
                    <Icon className={`h-5 w-5 ${plat.text}`} />
                    <span>{plat.label} Post</span>
                    <span className={`text-xs px-2 py-0.5 rounded-full border ${getStatusBadge(editPost.status).className} ml-2`}>
                      {getStatusBadge(editPost.status).label}
                    </span>
                  </>
                );
              })()}
            </DialogTitle>
          </DialogHeader>
          {editPost && (
            <div className="space-y-4">
              {/* Image section */}
              <div>
                <label className="text-xs font-medium text-muted-foreground mb-1 block">Image</label>
                <input
                  ref={editPostFileRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  className="hidden"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      setEditPostImage(file);
                      setEditPostImagePreview(URL.createObjectURL(file));
                    }
                    e.target.value = '';
                  }}
                />
                {(editPostImagePreview || editPost.image_url) ? (
                  <div className="relative">
                    <img
                      src={editPostImagePreview || editPost.image_url!}
                      alt=""
                      className="w-full max-h-48 object-cover rounded-lg border"
                    />
                    <div className="absolute top-2 right-2 flex gap-1">
                      <Button
                        size="icon"
                        variant="secondary"
                        className="h-7 w-7"
                        title="Replace image"
                        onClick={() => editPostFileRef.current?.click()}
                      >
                        <Upload className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                    {editPostImagePreview && (
                      <span className="absolute bottom-2 left-2 text-[10px] bg-black/60 text-white px-2 py-0.5 rounded">
                        New image (unsaved)
                      </span>
                    )}
                  </div>
                ) : (
                  <Button variant="outline" size="sm" className="w-full" onClick={() => editPostFileRef.current?.click()}>
                    <Upload className="h-4 w-4 mr-1" /> Upload Image
                  </Button>
                )}
              </div>
              <Textarea
                value={editContent}
                onChange={(e) => setEditContent(e.target.value)}
                rows={6}
                className="text-sm"
              />
              <div>
                <label className="text-xs font-medium text-muted-foreground mb-1 block">Scheduled For</label>
                <Input
                  type="datetime-local"
                  value={editPost.scheduled_for ? toLocalISOString(new Date(editPost.scheduled_for)) : ''}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (val) {
                      updateSchedule(editPost.id, new Date(val).toISOString());
                      setEditPost({ ...editPost, scheduled_for: new Date(val).toISOString() });
                    }
                  }}
                  className="text-sm"
                />
              </div>
              <div className="text-xs text-muted-foreground">
                Created: {new Date(editPost.created_at).toLocaleString()}
                {editPost.published_at && <> | Published: {new Date(editPost.published_at).toLocaleString()}</>}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditPost(null)}>Cancel</Button>
            <Button onClick={saveEdit} disabled={saving}>
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Quick Post Dialog ── */}
      <Dialog open={quickPostOpen} onOpenChange={(open) => {
        setQuickPostOpen(open);
        if (!open) { setQuickPostImage(null); setQuickPostImagePreview(null); }
      }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Quick Post</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-xs font-medium text-muted-foreground mb-1 block">Platform</label>
              <Select value={quickPostData.platform} onValueChange={(v) => setQuickPostData(d => ({ ...d, platform: v }))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="facebook">Facebook</SelectItem>
                  <SelectItem value="instagram">Instagram</SelectItem>
                  <SelectItem value="linkedin">LinkedIn</SelectItem>
                  <SelectItem value="linkedin_personal">LinkedIn (Brian)</SelectItem>
                  <SelectItem value="linkedin_company">LinkedIn (Company)</SelectItem>
                  <SelectItem value="youtube">YouTube</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground mb-1 block">Content</label>
              <Textarea
                value={quickPostData.content}
                onChange={(e) => setQuickPostData(d => ({ ...d, content: e.target.value }))}
                rows={5}
                placeholder="Write your post content..."
                className="text-sm"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground mb-1 block">Image (optional)</label>
              <input
                ref={quickPostFileRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    setQuickPostImage(file);
                    setQuickPostImagePreview(URL.createObjectURL(file));
                  }
                  e.target.value = '';
                }}
              />
              {quickPostImagePreview ? (
                <div className="relative">
                  <img src={quickPostImagePreview} alt="Preview" className="w-full max-h-40 object-cover rounded-lg border" />
                  <Button
                    size="icon"
                    variant="destructive"
                    className="absolute top-2 right-2 h-7 w-7"
                    onClick={() => { setQuickPostImage(null); setQuickPostImagePreview(null); }}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              ) : (
                <Button variant="outline" size="sm" className="w-full" onClick={() => quickPostFileRef.current?.click()}>
                  <Upload className="h-4 w-4 mr-1" /> Upload Image
                </Button>
              )}
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground mb-1 block">Schedule For (optional)</label>
              <Input
                type="datetime-local"
                value={quickPostData.scheduled_for}
                onChange={(e) => setQuickPostData(d => ({ ...d, scheduled_for: e.target.value }))}
                className="text-sm"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setQuickPostOpen(false)}>Cancel</Button>
            <Button onClick={createQuickPost} disabled={saving || !quickPostData.content.trim()}>
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Plus className="h-4 w-4 mr-1" />}
              Create Post
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
